import { useMultipleFeatureFlags } from '@agentnet/feature-flags';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { ArrowBack, CheckCircleOutline as CheckCircleOutlineIcon } from '@material-ui/icons';
import { MarketplaceProduct } from 'api/admin/types';
import { TitleMonitoringConfig } from 'api/titleMonitoring/types';
import {
  CONTACT_VALIDATION_PRODUCT_CODE,
  FRAUD_RAPID_RESPONSE_ASSET_ID,
  IDENTITY_VERIFICATION_PRODUCT_CODE,
  TITLE_MONITORING_PRODUCT_CODE,
  WIRE_VALIDATION_PRODUCT_CODE,
} from 'features/help/constants';
import { useAssetQuery } from 'features/help/queries/useAssetQuery';
import { useFraudToolDetailsQuery } from 'features/help/queries/useFraudToolDetailsQuery';
import { useMessagingQuery } from 'features/help/queries/useMessagingQuery';
import { FraudToolDetails } from 'features/help/types/fraudToolDetails';
import { Message } from 'features/help/types/messagingCollection';
import { UserRoleTypes } from 'features/admin/constants';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import { useMarketPlace } from 'hooks/useMarketPlace';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import ContactUsDialog from 'ui-kit/components/modal/ContactUsDialog';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import { normalizeServiceName } from 'utilities/utilities';
import { FeatureDetailsCards } from './FeatureDetailsCards';
import type { ConfigurationListItem } from './ConfigurationUnderwriterList';
import { toConfigurationListItems } from './icons';
import { ManageUnderwritersDrawer } from './ManageUnderwritersDrawer';
import { UNDERWRITER_PRODUCT_TEST_IDS } from './feature-details-tabs';
import IntegrationBanner from './IntegrationBanner';
import type { StatusChipConfig } from './IntegrationCard';
import { useTitleMonitoringConfiguration } from './titleMonitoring/useTitleMonitoringConfiguration';
import {
  BannerAction,
  FeatureDetailsDialog,
  FeatureDetailsDialogs,
  resolveBannerAction,
  toFeatureDetailsDialog,
} from './modals';
import {
  getFraudPreventionServiceByProductCode,
  getFraudPreventionServiceDisplayName,
} from './modals/fraudPreventionServices';

interface ServiceDetailsProps {
  goBackAction?: () => void;
  selectedService: MarketplaceProduct;
  marketplaceServices?: MarketplaceProduct[] | null;
  refreshServiceList: () => Promise<void>;
  ServiceIcon: JSX.Element;
  allDescriptions: Record<string, Message | null>;
  titleMonitoringConfig?: TitleMonitoringConfig | null;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    verticalSpacing: {
      marginBottom: theme.spacing(3),
    },
    linkText: {
      textDecoration: 'underline',
      color: theme.palette.primary.main,
      cursor: 'pointer',
      display: 'block',
      marginTop: theme.spacing(1),
      '&:hover': {
        color: theme.palette.secondary.main,
      },
    },
    dataCard: {
      height: 'fit-content',
    },
  }),
);

const ServiceDetails: FC<ServiceDetailsProps> = ({
  selectedService,
  goBackAction,
  marketplaceServices,
  refreshServiceList,
  ServiceIcon,
  titleMonitoringConfig,
}) => {
  const classes = useStyles();
  const { addSnackbarMessage } = useSnackBars();
  const { addGlobalMsg } = useGlobalMessages();
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { productName, description, isEnabled, category, productCode, isFirmEnablementRequired } = selectedService;
  const [activeDialog, setActiveDialog] = useState<FeatureDetailsDialog | null>(null);
  const [contactUsModal, setContactUsModal] = useState<boolean>(false);
  const [isCompletingEnableFlow, setIsCompletingEnableFlow] = useState(false);
  const [pendingEnableClose, setPendingEnableClose] = useState(false);
  const [isManageUnderwritersDrawerOpen, setIsManageUnderwritersDrawerOpen] = useState(false);
  const [isManageUnderwritersFromEnableFlow, setIsManageUnderwritersFromEnableFlow] = useState(false);
  const [isCompletingDisableFlow, setIsCompletingDisableFlow] = useState(false);
  const [pendingDisableClose, setPendingDisableClose] = useState(false);
  const {
    updateMarketPlaceServiceStatus,
    updateServiceStatus,
    fetchFirmUnderwriters,
    fetchMarketPlaceServicesResults,
    fetchFirmUnderwritersResults,
    fetchFirmUnderwritersStatus,
  } = useMarketPlace();
  const [acknowledgementMessage, setAcknowledgementMessage] = useState<Message | null>();
  const [descriptionMessage, setDescriptionMessage] = useState<Message | null>();
  const [fraudToolDetails, setFraudToolDetails] = useState<FraudToolDetails | null>();
  const [rapidResponsePdfUrl, setRapidResponsePdfUrl] = useState<string | null>(null);

  const isTitleMonitoring = productCode === TITLE_MONITORING_PRODUCT_CODE;
  const titleMonitoring = useTitleMonitoringConfiguration(titleMonitoringConfig);
  const [nonFaUw] = useMultipleFeatureFlags(FeatureFlag.NON_FA_UW_ANS_679_ENABLED);

  const isUnderwriterProduct = [
    CONTACT_VALIDATION_PRODUCT_CODE,
    IDENTITY_VERIFICATION_PRODUCT_CODE,
    WIRE_VALIDATION_PRODUCT_CODE,
  ].includes(productCode);

  const isNonFaUwEnabled = !!nonFaUw.value;
  const showUnderwriterUi = nonFaUw.ready && isNonFaUwEnabled && isUnderwriterProduct;

  // First American employees may view a firm's fraud prevention add-ons but cannot turn them on or off.
  const isStaffBlockedFromFraudProduct = isUnderwriterProduct && profileCtx.profile?.roleTypeId === UserRoleTypes.Staff;

  // TODO: Right now only Title Monitoring is depends on isFirmEnablementRequired flag
  // We need to revisit this once we have to use this flag for other products.
  const isBannerActionDisabled = showUnderwriterUi
    ? isTitleMonitoring && !isFirmEnablementRequired
    : !isFirmEnablementRequired;

  const enabledFraudPreventionProductCodes = useMemo(() => {
    if (!marketplaceServices) {
      return [];
    }

    const fraudProductCodes = new Set([
      CONTACT_VALIDATION_PRODUCT_CODE,
      IDENTITY_VERIFICATION_PRODUCT_CODE,
      WIRE_VALIDATION_PRODUCT_CODE,
    ]);

    return marketplaceServices
      .filter((service) => fraudProductCodes.has(service.productCode) && service.isEnabled)
      .map((service) => service.productCode);
  }, [marketplaceServices]);

  const handleCloseDialog = useCallback(() => {
    setActiveDialog(null);
  }, []);

  const reconcileFeatureState = useCallback(async () => {
    await refreshServiceList();

    if (showUnderwriterUi) {
      await fetchFirmUnderwriters();
    }
  }, [fetchFirmUnderwriters, refreshServiceList, showUnderwriterUi]);

  const showProductStatusMessage = useCallback(
    (code: string, enabled: boolean) => {
      const productNameToDisplay =
        marketplaceServices?.find((service) => service.productCode === code)?.productName ??
        getFraudPreventionServiceByProductCode(code)?.productName ??
        productName;

      addSnackbarMessage({
        message: `${getFraudPreventionServiceDisplayName(productNameToDisplay)} is ${enabled ? 'enabled' : 'disabled'}`,
        type: 'success',
      });
    },
    [addSnackbarMessage, marketplaceServices, productName],
  );

  const showStaffBlockedMessage = useCallback(
    (action: 'enable' | 'disable') => {
      addGlobalMsg({
        message: `FA Employees can not ${action} ${getFraudPreventionServiceDisplayName(productName)} for Firms`,
        type: 'error',
      });
    },
    [addGlobalMsg, productName],
  );

  const completeEnableFlow = useCallback(
    async (additionalProductCodes: string[] = []) => {
      if (isCompletingEnableFlow) {
        return;
      }

      if (isStaffBlockedFromFraudProduct) {
        showStaffBlockedMessage('enable');
        return;
      }

      setIsCompletingEnableFlow(true);
      const productCodesToEnable = Array.from(new Set([productCode, ...additionalProductCodes]));
      const failedProductCodes: string[] = [];

      try {
        for (const code of productCodesToEnable) {
          try {
            await updateMarketPlaceServiceStatus({
              productCode: code,
              isEnabled: !isEnabled,
              isEulaAccepted: !isEnabled,
            });
            showProductStatusMessage(code, !isEnabled);
          } catch (error) {
            failedProductCodes.push(code);
          }
        }

        if (failedProductCodes.length > 0) {
          handleCloseDialog();
          await refreshServiceList();
          return;
        }

        if (isTitleMonitoring) {
          await titleMonitoring.createConfigIfMissing();
        }

        // The dialog is dismissed before the refresh because the refresh empties the service
        // list while it is in flight, which would repopulate the dialog with every product.
        handleCloseDialog();

        // Enabling a product changes which products are on, not the underwriter roster the
        // drawer edits, so the list already loaded on mount is reused. Refetching it here would
        // blank the drawer while it opens.
        await refreshServiceList();
        setPendingEnableClose(true);
      } finally {
        setIsCompletingEnableFlow(false);
      }
    },
    [
      handleCloseDialog,
      isCompletingEnableFlow,
      isEnabled,
      isStaffBlockedFromFraudProduct,
      isTitleMonitoring,
      productCode,
      refreshServiceList,
      showProductStatusMessage,
      showStaffBlockedMessage,
      titleMonitoring,
      updateMarketPlaceServiceStatus,
    ],
  );

  const handleEnableAcknowledgementAccept = useCallback(() => {
    setActiveDialog(FeatureDetailsDialog.EnableAdditionalServices);
  }, []);

  const handleEnableAdditionalServicesCancelOrClose = useCallback(() => {
    handleCloseDialog();
  }, [handleCloseDialog]);

  const handleEnableAdditionalServicesContinue = useCallback(
    (selectedProductCodes: string[]) => {
      void completeEnableFlow(selectedProductCodes);
    },
    [completeEnableFlow],
  );

  const disableService = useCallback(async () => {
    await updateMarketPlaceServiceStatus({
      productCode,
      isEnabled: false,
      isEulaAccepted: false,
    });
  }, [productCode, updateMarketPlaceServiceStatus]);

  const completeDisableFlow = useCallback(async () => {
    if (isCompletingDisableFlow) {
      return;
    }

    if (isStaffBlockedFromFraudProduct) {
      showStaffBlockedMessage('disable');
      return;
    }

    setIsCompletingDisableFlow(true);
    try {
      await disableService();
      showProductStatusMessage(productCode, false);
      setPendingDisableClose(true);
    } catch (error) {
      // Global error messaging is handled by useMarketPlace updateServiceStatusErrors.
      handleCloseDialog();
    } finally {
      setIsCompletingDisableFlow(false);
      await reconcileFeatureState();
    }
  }, [
    disableService,
    handleCloseDialog,
    isCompletingDisableFlow,
    isStaffBlockedFromFraudProduct,
    reconcileFeatureState,
    showProductStatusMessage,
    showStaffBlockedMessage,
    productCode,
  ]);

  const handleOtherUnderwritersDisableConfirm = useCallback(() => {
    void completeDisableFlow();
  }, [completeDisableFlow]);

  const handleConfirmDialog = useCallback(async () => {
    if (isStaffBlockedFromFraudProduct) {
      showStaffBlockedMessage(isEnabled ? 'disable' : 'enable');
      return;
    }

    const productStatus = {
      productCode: productCode,
      isEnabled: !isEnabled,
      isEulaAccepted: !isEnabled,
    };
    await updateMarketPlaceServiceStatus(productStatus);
    showProductStatusMessage(productCode, !isEnabled);
    await refreshServiceList();
    handleCloseDialog();
  }, [
    handleCloseDialog,
    productCode,
    isEnabled,
    isStaffBlockedFromFraudProduct,
    refreshServiceList,
    showProductStatusMessage,
    showStaffBlockedMessage,
    updateMarketPlaceServiceStatus,
  ]);

  useEffect(() => {
    if (!pendingEnableClose) {
      return;
    }

    if (updateServiceStatus === 'success') {
      setPendingEnableClose(false);
      handleCloseDialog();

      if (showUnderwriterUi) {
        setIsManageUnderwritersDrawerOpen(true);
        setIsManageUnderwritersFromEnableFlow(true);
      }
    }

    if (updateServiceStatus === 'error') {
      setPendingEnableClose(false);
    }
  }, [handleCloseDialog, pendingEnableClose, showUnderwriterUi, updateServiceStatus]);

  useEffect(() => {
    if (!pendingDisableClose) {
      return;
    }

    if (updateServiceStatus === 'success') {
      setPendingDisableClose(false);
      handleCloseDialog();
    }

    if (updateServiceStatus === 'error') {
      setPendingDisableClose(false);
      handleCloseDialog();
    }
  }, [handleCloseDialog, pendingDisableClose, refreshServiceList, updateServiceStatus]);

  const handleBannerClick = useCallback(async () => {
    const action = resolveBannerAction({
      isEnabled,
      isNonFaUwEnabled,
      showUnderwriterUi,
      isTitleMonitoring,
    });

    if (action === BannerAction.DirectEnable) {
      await completeEnableFlow();
      return;
    }

    const dialog = toFeatureDetailsDialog(action);
    if (dialog) {
      setActiveDialog(dialog);
    }
  }, [completeEnableFlow, isEnabled, isNonFaUwEnabled, isTitleMonitoring, showUnderwriterUi]);

  const handleContactUsClick = useCallback(() => {
    setContactUsModal(true);
  }, []);

  const handleManageUnderwritersClick = useCallback(() => {
    setIsManageUnderwritersDrawerOpen(true);
    setIsManageUnderwritersFromEnableFlow(false);
  }, []);

  const closeManageUnderwritersDrawer = useCallback(() => {
    setIsManageUnderwritersDrawerOpen(false);
    setIsManageUnderwritersFromEnableFlow(false);
  }, []);

  const showUnderwritersEnabledMessage = useCallback(() => {
    addSnackbarMessage({
      message: 'Product Enabled for Other Underwriters',
      type: 'success',
    });
  }, [addSnackbarMessage]);

  // Dismissing the drawer during the enable flow still leaves the product enabled,
  // so the product page is confirmed and refreshed as if the step had been completed.
  const handleCloseManageUnderwritersDrawer = useCallback(async () => {
    closeManageUnderwritersDrawer();

    if (isManageUnderwritersFromEnableFlow) {
      showUnderwritersEnabledMessage();
      await reconcileFeatureState();
    }
  }, [
    closeManageUnderwritersDrawer,
    isManageUnderwritersFromEnableFlow,
    reconcileFeatureState,
    showUnderwritersEnabledMessage,
  ]);

  const handleManageUnderwritersSaveSuccess = useCallback(async () => {
    closeManageUnderwritersDrawer();
    showUnderwritersEnabledMessage();
    await reconcileFeatureState();
  }, [closeManageUnderwritersDrawer, reconcileFeatureState, showUnderwritersEnabledMessage]);

  // The drawer's own useMarketPlace instance raises the global error message for the failed save.
  const handleManageUnderwritersSaveError = useCallback(async () => {
    closeManageUnderwritersDrawer();
    await reconcileFeatureState();
  }, [closeManageUnderwritersDrawer, reconcileFeatureState]);

  const configurationItems = useMemo((): ConfigurationListItem[] => {
    const underwriterNames =
      fetchFirmUnderwritersResults?.underwriters
        .filter((underwriter) => underwriter.IsSelected !== false)
        .map((underwriter) => underwriter.Name) ?? [];

    return toConfigurationListItems(underwriterNames);
  }, [fetchFirmUnderwritersResults]);

  // fetchFirmUnderwriters comes from useAsync, and the underlying
  // firmUnderwriters function in useMarketPlace is recreated each render
  // , so including it in deps would cause repeated fetches hence exluding
  useEffect(() => {
    if (showUnderwriterUi) {
      fetchFirmUnderwriters();
    }
  }, [showUnderwriterUi]);

  const isWireValidation = productCode === WIRE_VALIDATION_PRODUCT_CODE;

  useEffect(() => {
    if (!nonFaUw.ready) {
      return;
    }

    // useMessagingQuery/useFraudToolDetailsQuery/useAssetQuery are plain async Contentful
    // fetchers, not React hooks; the use* prefix is an existing convention in features/help/queries.
    const fetchMessages = async () => {
      setAcknowledgementMessage(null);
      setDescriptionMessage(null);
      setFraudToolDetails(null);
      setRapidResponsePdfUrl(null);

      const serviceSlug = `feature-${normalizeServiceName(productName)}`;
      const fraudToolSlug = `${serviceSlug}-underwriter`;

      if (showUnderwriterUi) {
        const ack = await useMessagingQuery('fraud-prevention-tools-acknowledgment');
        const fraudTool = await useFraudToolDetailsQuery(fraudToolSlug);
        setFraudToolDetails(fraudTool.data);
        setAcknowledgementMessage(ack.data);
      } else {
        const desc = await useMessagingQuery(serviceSlug);
        setDescriptionMessage(desc.data);
      }

      // Fetch Fraud Rapid Response PDF URL for Wire Validation
      if (productCode === WIRE_VALIDATION_PRODUCT_CODE) {
        const assetResponse = await useAssetQuery(FRAUD_RAPID_RESPONSE_ASSET_ID);
        if (assetResponse.data?.url) {
          setRapidResponsePdfUrl(assetResponse.data.url);
        }
      }
    };

    fetchMessages();
  }, [productCode, productName, showUnderwriterUi, nonFaUw.ready]);

  const testIdPrefixBanner = `${normalizeServiceName(productName)}-button-${isEnabled ? 'disable' : 'enable'}`;
  const testIdPrefixButton = `${normalizeServiceName(productName)}-${isEnabled ? 'disable' : 'enable'}`;
  const testIdPrefixDialog = `${normalizeServiceName(productName)}-${isEnabled ? 'disable' : 'enable'}-button-`;
  const underwriterActionButtonTestId = showUnderwriterUi
    ? isEnabled
      ? UNDERWRITER_PRODUCT_TEST_IDS.disableButton
      : UNDERWRITER_PRODUCT_TEST_IDS.enableButton
    : undefined;

  const bannerStatusChips: StatusChipConfig[] | undefined = showUnderwriterUi
    ? [
        {
          label: 'First American Enabled',
          variant: 'success',
          icon: <CheckCircleOutlineIcon fontSize="small" />,
          testId: `${testIdPrefixButton}-first-american-enabled-chip`,
        },
      ]
    : undefined;

  if (!nonFaUw.ready) {
    return <LoadingSpinner variant="circle" status="pending" />;
  }

  return (
    <>
      <AgentNetButton
        variant="text"
        size="small"
        color="primary"
        startIcon={<ArrowBack />}
        onClick={goBackAction && goBackAction}
        data-testid="add-ons-link-add-ons"
        className={classes.verticalSpacing}
      >
        Add-Ons
      </AgentNetButton>

      <div className={classes.verticalSpacing} data-testid={testIdPrefixBanner}>
        <IntegrationBanner
          title={productName.includes('Enhanced') ? 'Identity Verification' : productName}
          description={description}
          icon={ServiceIcon}
          category={category}
          enabled={isEnabled}
          dataTestId={showUnderwriterUi ? undefined : testIdPrefixButton}
          actionButtonDataTestId={underwriterActionButtonTestId}
          disabled={isBannerActionDisabled}
          enableLabel={showUnderwriterUi ? 'Enable for Other Underwriters' : 'Enable'}
          disableLabel={showUnderwriterUi ? 'Disable for Other Underwriters' : 'Disable'}
          statusChips={bannerStatusChips}
          onClick={handleBannerClick}
        />
      </div>

      <FeatureDetailsCards
        showTabbedContent={isTitleMonitoring || isNonFaUwEnabled}
        selectedService={selectedService}
        descriptionMessage={descriptionMessage}
        fraudToolDetails={fraudToolDetails}
        isWireValidation={isWireValidation}
        rapidResponsePdfUrl={rapidResponsePdfUrl}
        onContactUsClick={handleContactUsClick}
        isTitleMonitoring={isTitleMonitoring}
        titleMonitoringConfiguration={titleMonitoring}
        configurationItems={configurationItems}
        onManageUnderwritersClick={handleManageUnderwritersClick}
        showUnderwriterUi={showUnderwriterUi}
      />

      <FeatureDetailsDialogs
        activeDialog={activeDialog}
        selectedService={selectedService}
        enabledFraudPreventionProductCodes={enabledFraudPreventionProductCodes}
        testIdPrefixDialog={testIdPrefixDialog}
        updateServiceStatus={updateServiceStatus}
        isCompletingEnableFlow={isCompletingEnableFlow}
        isCompletingDisableFlow={isCompletingDisableFlow}
        acknowledgementMessage={acknowledgementMessage}
        onClose={handleCloseDialog}
        onEnableAcknowledgementAccept={handleEnableAcknowledgementAccept}
        onEnableAdditionalServicesCancelOrClose={handleEnableAdditionalServicesCancelOrClose}
        onEnableAdditionalServicesContinue={handleEnableAdditionalServicesContinue}
        onOtherUnderwritersDisableConfirm={handleOtherUnderwritersDisableConfirm}
        onConfirm={handleConfirmDialog}
      />
      <ContactUsDialog open={contactUsModal} onClose={() => setContactUsModal(false)} />
      {showUnderwriterUi && (
        <ManageUnderwritersDrawer
          open={isManageUnderwritersDrawerOpen}
          onClose={handleCloseManageUnderwritersDrawer}
          onSaveSuccess={handleManageUnderwritersSaveSuccess}
          onSaveError={handleManageUnderwritersSaveError}
          underwriters={fetchFirmUnderwritersResults?.underwriters ?? []}
          loading={fetchFirmUnderwritersStatus === 'pending'}
        />
      )}
    </>
  );
};

export default ServiceDetails;
